import { Measure, WeightGrid } from '@wizeoo/akm-message-lib';
import { FeiVO } from '../../vo/workingdata/fei.vo';
import { WizConstants } from '../../model/constants';
import { MeasureCoreEngine } from '../computer/measure.core.engine';
import { BaselineEngine } from './baseline.engine';
import { MeasMode } from '../computer/measure.mode.obj';
import { Model } from '../../model/model';
import { MeasureVO } from '../../vo/workingdata/measure.vo';

/**
 *gestion des measures pour le moteur
 * Contient toutes les measures chargées pour le périmètre de l'utilisateur
 */
export class MeasureEngine{

  protected _model: Model = null;
  constructor(model: Model) {
    this._model = model;
  }

    /** Liste de mesures, brute */
    public getMeasureList(): MeasureVO[] {
        return this._model.measureList;
    }

    // map des mesures vision => stepLevel(0 = vision) =>  shortCode = measureVO
    public getMeasureMap(): Map<string, Map<number, Map<string, MeasureVO>>> {
        return this._model.measureMap;
    }
    public setMeasureMap(value: Map<string, Map<number, Map<string, MeasureVO>>>) {
      this._model.measureMap = value;
    }

    /**
     * Récupération de la mesure associée au code donné si elle existe, null sinon
     * @param pCode Le code de la mesure recherchée
     * @param isWeight, pour recherche dans le dico de fake
     */
    public getMeasure(pVisionCode: string, pStepLevel: number, pShortCode: string, isWeight: boolean = false): MeasureVO {
      if (isWeight) {
          return this._model.weightGridEngine.getWeightAsMeasureMap()?.get(pShortCode);
      } else {
          const map = this.getMeasureMap();
          let meas: MeasureVO = null;
          if (
              this.getMeasureMap().has(pVisionCode) &&
              this.getMeasureMap().get(pVisionCode).has(pStepLevel)
          ) {
              // prise de la mesure sur le step
              meas = this.getMeasureMap().get(pVisionCode).get(pStepLevel).get(pShortCode);
          }
          if (!meas) {
              if (this.getMeasureMap().has(pVisionCode) && this.getMeasureMap().get(pVisionCode).has(0)) {
                  // si pas trouvé, prise sur vision
                  meas = this.getMeasureMap().get(pVisionCode).get(0).get(pShortCode);
              }
          }
          return meas;
      }
    }

    /**
     * Alimentation des mesures du modèle
     * @param measures: Measure[]
     */
    public feedMeasures(measures: Measure[]): void {
        const result: MeasureVO[] = measures.map((meas) => new MeasureVO(meas));

        const map: Map<string, Map<number, Map<string, MeasureVO>>> = new Map<string, Map<number, Map<string, MeasureVO>>>();
        result.forEach((meas: MeasureVO) => {
            MeasureCoreEngine.addMeasureToMap(map, meas);
        });

        this._model.measureList = result;
        this.setMeasureMap(map);
    }

  // /**
  //  * matrice de MeasureMode par niveau (pour déterminer les dépendances des Measures)
  //  * Map[niveau (commence à 1)] = Map[key#Mode] = MeasureMode
  //  */
  // public matrixByLevel: Map<number, Map<string, MeasureMode>> = null;
  // // matrice inversée, pour un key#mode, retourne le [level, MeasureMode]
  // public matrixByCode: Map<string, [number, MeasureMode]> = null;

  public buildFeiMatrix(fei: FeiVO): void {
      // construction de la matrice de measures/ constants / weightGrids
      const measures: Measure[] = BaselineEngine.getStepMeasures(fei.vision, fei.visionStep); //  fei.visionStep.getMeasuresVO(); // mesures au step avec override possible
      //  TODO TODO tenir compte des WeightGrids
      this.buildMeasureMatrix(fei, measures, null);
  }

  /**
   * Construction de la matrice de mesures, retourne une map sous la forme : <PVI#I, MeasMode> Contient les constantes, les weightGrids, les
   * baselineValue et les measures calculées
   *
   * Chaque mesure connait ses dépendances
   *
   * @param measures  la liste des mesures de la vision
   * @param wg les weightGrids de la BU
   * @return une pair : [{ [key: string]: MeasMode } , Map<level, Map<key, MeasMode>>] Map des mesures construite
   */
   private buildMeasureMatrix(fei: FeiVO, measures: Measure[], wg: WeightGrid[]): void {

    
    // initialisation de la structure des mesures modes en erreur
    // this.initMeasureModeErrors();

    // const measures: {[key: string]: Measure} = vision.measures;
    const leveledMatrix: Map<number, Map<string, MeasMode>> = new Map<number, Map<string, MeasMode>>();
    leveledMatrix.set(1, new Map<string, MeasMode>());

    // la map des measures déjà identifiées et ventilées (temporaire, pour traitement)
    const hitMap: Map<string, MeasMode> = new Map<string, MeasMode>();
    // la map des measures non attribuées (temporaire, doit être vide en fin de traitement, sinon, référence circulaire
    let pendingMap: Map<string, MeasMode> = new Map<string, MeasMode>();
    // les modes à ventiler
    // deprecated, les mesures portent leurs modes actifs (champ mode)
    // const modes: string[] = [ FormulaCoreEngine.MEASURE_MODE_FROM,
    //                           FormulaCoreEngine.MEASURE_MODE_INTERACT,
    //                           FormulaCoreEngine.MEASURE_MODE_SUBMIT,
    //                           FormulaCoreEngine.MEASURE_MODE_TO];

    // instance mm en cours de création
    let item: MeasMode;

    // ### 1er tour, les measures de type CONSTANT
    measures.forEach((measure: Measure) => {
      if (measure.mode === WizConstants.MEASURE_MODE_CONSTANT) {
        const constMm: MeasMode = MeasureCoreEngine.measureModeFromConstant(fei, measure);
        hitMap.set(constMm.code, constMm);
        leveledMatrix.get(1).set(constMm.code, constMm); // ajout dans la Map nivelée
      }
    });

    if (!!wg) {
      // seulement s'il y a des WGs
      wg.forEach((wgi: WeightGrid) => {
        // ajout des weightGrids en niveau 1
        const wgMm: MeasMode = MeasureCoreEngine.measureModeFromWeightGrid(fei, wgi);
        hitMap.set(wgMm.code, wgMm);
        leveledMatrix.get(1).set(wgMm.code, wgMm); // ajout dans la Map nivelée
      });
    }

    let idx: number = 1; // initialement, pas de dépendance, on est dans la pile 1

    // ### 2eme tour

    // création des measureModes sur la base des measures et première ventilation
    // dans result / pending
    measures.forEach((measure: Measure) => {

      if (measure.mode !== WizConstants.MEASURE_MODE_CONSTANT && !!measure.mode) {
        measure.mode.split('').forEach((mode: string) => {
          // const fullCode: string = measure.code + FormulaCoreEngine.MODE_SEPARATOR + mode;
          // if (!hitMap.has(fullCode)) { // pas dans la Map (constantes)
          item = MeasureCoreEngine.measureModeFromFormula(fei, measure, mode);
          // if(fei.visionStepLevel === 1 && item.code ==='MES_STOCKCIBLEMAGASIN#T') {
          //   console.log('measure', item.code, item.formula);
          // }

          if (!item.deps) {
            // && Object.keys(item.dependencies).length > 0) { // aucune dépendance, OK
            // console.log('hit', item.code);
            hitMap.set(item.code, item);
            leveledMatrix.get(1).set(item.code, item); // ajout dans la Map nivelée
          } else {
            // console.log('leveledMatrix measure deps', item.code, item.deps);
            pendingMap.set(item.code, item);
          }
          // }
        });
      }
    });

    // ### Autres tours jusqu'à vider la map de pending
    // prise de la taille des pending. Si à un tour, elle ne baisse pas, j'ai une
    // référence circulaire
    let lastTurnPending: number = pendingMap.size;
    let stillOrphans: boolean = lastTurnPending > 0;
    while (stillOrphans) {
      idx++; // on incrémente le compteur de niveau
      // alimentation du jeu de travail et vidange de la map des pending
      const stillWorking: MeasMode[] = Array.from(pendingMap.values());
      pendingMap = new Map<string, MeasMode>();
      // On traite les mesures restantes
      stillOrphans = this.getMeasureFromPendingMap(
        stillWorking,
        pendingMap,
        hitMap,
        lastTurnPending,
        leveledMatrix,
        idx
      );
      // mise à jour de la taille suite au dernier traitement
      lastTurnPending = pendingMap.size;

      // TODO Lever une exception si on ne peut pas ventiler toutes les mesures ("ref ciculaire" => stillOrphans == null) ?
    }
    
    const flatMatrix: Map<string, MeasMode> = new Map<string, MeasMode>(); // on déplie la matrice
    const orderedMatrix: MeasMode[] = [];

    let omm: MeasMode;
    Array.from(leveledMatrix.keys())
      .sort((a, b) => a - b)
      .forEach((lvl: number) => {
        const premat: Map<string, MeasMode> = leveledMatrix.get(lvl);
        Array.from(premat.keys()).forEach((key) => {
          omm = premat.get(key);
          if (
            !!fei.toolMode &&
            (omm.mm === WizConstants.MEASURE_MODE_FROM ||
              omm.mm === WizConstants.MEASURE_MODE_TO)
          ) {
            // sample de pivot, on fait rien
          } else {
            orderedMatrix.push(omm); // mmodes ordonnés par level, pour résolution sans conflit
            flatMatrix.set(key, omm);
          }
        });
      });

    fei.matrix = flatMatrix;
    fei.leveledMatrix = leveledMatrix;
    fei.orderedMatrix = orderedMatrix;

    
    let translatableEntries: Set<string> = new Set(); // gestion des mesures traductibles (toutes)
    let translatableModeMatrix: MeasMode[] = []; // gestion des mesures traductibles: les originelles

    orderedMatrix.forEach(mm => {
      if(mm.useLocale) { // je suis une fonction traductible, ajout direct
        translatableEntries.add(mm.code);
        translatableModeMatrix.push(mm);
      }

      // on ajoute toute la pile de dépendance dans translatableEntries, pou détection rapide dans les fonctions
      mm.deps?.forEach(dep => { // si l'une de mes dépendances est traductible, c'est contagieux
        if(translatableEntries.has(dep) && !translatableEntries.has(mm.code)) {
          translatableEntries.add(mm.code);
          // translatableModeMatrix.push(mm);
        }
      });
    });

    // console.log('[LMA TRS]translatables', translatableEntries, translatableModeMatrix);
    fei.translatableEntries = translatableEntries;
    fei.translatableModeMatrix = translatableModeMatrix;
    
    
    // ajout des dépendances par niveaux
    // on contrôle quand même que le step existe...

    let maxStep: number = BaselineEngine.getVisionStepCount(fei.vision);

    fei.matrix.forEach((mm: MeasMode) => {
      if (!!mm.stepDeps) {
        [...mm.stepDeps.values()].forEach((lvl: number) => {
          if(lvl > 0 && lvl <= maxStep && lvl !== fei.visionStepLevel) {
            //console.log('stepDeps from: ', fei.visionStepLevel, ' on: ' , lvl, ' by: ', mm.m.shortCode);
            fei.requiredStep.add(lvl);
          }
        });
      }
    });
  }

  /**
   * Traitement des mesures en attente Pour chaque mesure en attente, on va boucler sur ses dépendances. Si la map des mesures déjà traitées ne contient
   * pas une dépendance, on ajoute la mesure à la liste des mesures en attente et on refera une boucle pour cette mesure. Si la map des mesures déjà
   * traitées contient la dépendance, on bascule la mesure en attente dans les mesures déjà traitées Retourne True s'il reste des mesures à traiter,
   * False si on traité toutes les mesures, Null si on a traité aucune mesure
   *
   * @param stillWorking    La map des mesures à attente
   * @param pendingMap      La map des mesures qui restent en attente à la fin du traitement
   * @param hitMap          La map des mesures déjà traitées
   * @param lastTurnPending Nombre de mesures en attente
   * @param leveledMatrix      l'objet de retour Map< number, Map<string, MeasureMode>>
   * @param idx             le compteur de niveau
   * @return Boolean True s'il reste des mesures à traiter, False si on traité toutes les mesures, Null si on a traité aucune mesure
   */
  private getMeasureFromPendingMap(
    stillWorking: MeasMode[],
    pendingMap: Map<string, MeasMode>,
    hitMap: Map<string, MeasMode>,
    lastTurnPending: number,
    leveledMatrix: Map<number, Map<string, MeasMode>>,
    idx: number
  ): boolean {
    let stillOrphans: boolean = true; // on part du principe qu'il reste des orphelins
    const justAdded: Map<string, MeasMode> = new Map<string, MeasMode>(); // formule ajoutée ce tour-ci. Elle ne peuvent pas être utilisées comme dépendance

    leveledMatrix.set(idx, new Map<string, MeasMode>()); // alimentation du niveau de map

    stillWorking.forEach((mm: MeasMode) => {
      let allIsOk: boolean = true; // tout va bien se passer... ou pas.
      [...mm.deps.values()].forEach((dependencyCode: string) => {
        if (justAdded.has(dependencyCode)) {
          // cette dépendance vient d'être ajoutée
          allIsOk = false; // KO
          pendingMap.set(mm.code, mm); // retour dans la map d'attente
        } else if (!hitMap.has(dependencyCode)) {
          // oups, la dépendance n'est pas encore dispo
          allIsOk = false; // KO
          // console.log('KO', mm.code, dependencyCode); // mm.formula,
          // resCons.push(mm.code + " "+ dependencyCode + " " + mm.formula  );
          pendingMap.set(mm.code, mm); // retour dans la map d'attente
        } else {
          // la dépendance est présente, je m'ajoute à sa pile d'héritage
          if (!hitMap.get(dependencyCode).legs) {
            hitMap.get(dependencyCode).legs = new Set<string>();
          }
          hitMap.get(dependencyCode).legs.add(mm.code);
        }
      });

      if (allIsOk) {
        // cette fois c'est OK
        // console.log('hit', mm.code, mm.formula);
        hitMap.set(mm.code, mm);
        leveledMatrix.get(idx).set(mm.code, mm); // ajout dans la Map nivelée

        // ajout à la map des récents
        justAdded.set(mm.code, mm);
      }
    });

    // fin du tour de boucle, reste-t-il des non-attribués
    // console.log('pend', pendingMap.size, hitMap.size);
    if (pendingMap.size === 0) {
      // job's done
      stillOrphans = false; // sortie de boucle
    } else {
      if (lastTurnPending === pendingMap.size) {
        Array.from(pendingMap.values()).forEach((item: MeasMode) => {
          // Ajout du measure mode dans la structure des mesures modes en erreur (car non ventilé)
          // this.addMeasureModeError(item);
          console.log('Missing or in error measure', item.mc, item.mm, '#', item.form);
        });
        // this.errorMMs = pendingMap;
        // console.log(resCons.join('\n').toString());
        stillOrphans = null; // on sort de force pour éviter les boucles infinies
      }
    }
    return stillOrphans;
  }

    /**
     * pour un MM donné, recherche de la pile complète de dépendances (les dépendances des enfants)
     * @param MeasMode le code du mesureMode cible
     */
    public measureModeFullDependencies(fei: FeiVO, measureCode: string): Map<string, MeasMode> {
        const result: Map<string, MeasMode> = new Map<string, MeasMode>();
        if (fei.matrix?.has(measureCode)) {
            const mm: MeasMode = fei.matrix.get(measureCode);
            this.recurseMMDependencies(fei, mm, result);
        }
        return result;
    }

    /** sous-routine de measureModeFullDependencies */
    private recurseMMDependencies(fei: FeiVO, mm: MeasMode, result: Map<string, MeasMode>): void {
        if (mm.deps?.size > 0) {

            [...mm.deps.values()].forEach((s: string) => {
                const dep: MeasMode = fei.matrix.get(s);
                if (!result.has(dep.code)) {
                    // déjà inscrite, inutile de reboucler
                    result.set(dep.code, dep);
                    this.recurseMMDependencies(fei, dep, result);
                }
            });
        }
    }

}
